import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Autocomplete,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller, useWatch } from 'react-hook-form';
import DataTable, { Column } from '../../components/common/DataTable';
import {
  sendPushNotification,
  getNotificationHistory,
  SendNotificationResponse,
} from '../../services/api/notificationApi';
import { PushNotificationData, NotificationHistory } from '../../types/common';
import { formatDateTime } from '../../utils/formatters';
import { getUsers } from '../../services/api/userApi';

const PushNotificationPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<{
    page: number;
    limit: number;
  }>({
    page: 0,
    limit: 20,
  });
  const [sendResult, setSendResult] = useState<SendNotificationResponse | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [userSearch, setUserSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm<PushNotificationData>({
    defaultValues: {
      title: '',
      body: '',
      targetType: 'all',
      targetUserIds: [],
    },
  });

  // targetType 값을 실시간으로 감시
  const targetType = useWatch({
    control,
    name: 'targetType',
    defaultValue: 'all',
  });

  // 사용자 목록 조회
  const { data: usersData } = useQuery({
    queryKey: ['users', userSearch],
    queryFn: () => getUsers({ search: userSearch, page: 1, limit: 50 }),
    enabled: targetType === 'specific',
  });

  const { data: historyData, isLoading } = useQuery({
    queryKey: ['notificationHistory', filters],
    queryFn: () => getNotificationHistory(filters),
  });

  const sendMutation = useMutation({
    mutationFn: sendPushNotification,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['notificationHistory'] });
      setSendResult(data);
      setSnackbar({
        open: true,
        message: `푸시 알림이 전송되었습니다. (성공: ${data.successCount}, 실패: ${data.failureCount})`,
        severity: 'success',
      });
      reset();
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '푸시 알림 전송에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleRowsPerPageChange = (limit: number) => {
    setFilters(prev => ({ ...prev, limit, page: 0 }));
  };

  const handleFormSubmit = (data: PushNotificationData) => {
    // targetUserIds를 배열로 변환 (쉼표로 구분된 문자열인 경우)
    const formattedData = {
      ...data,
      targetUserIds:
        data.targetType === 'specific' && typeof data.targetUserIds === 'string'
          ? (data.targetUserIds as string).split(',').map(id => id.trim())
          : data.targetUserIds,
    };
    sendMutation.mutate(formattedData);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const columns: Column<NotificationHistory>[] = [
    {
      id: 'title',
      label: '제목',
      minWidth: 200,
    },
    {
      id: 'targetType',
      label: '대상',
      minWidth: 100,
      format: (value: string) => {
        switch (value) {
          case 'all':
            return '전체 사용자';
          case 'enabled':
            return '알림 허용 사용자';
          case 'disabled':
            return '알림 비허용 사용자';
          case 'specific':
            return '특정 사용자';
          default:
            return value;
        }
      },
    },
    {
      id: 'targetCount',
      label: '대상 수',
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'successCount',
      label: '성공',
      minWidth: 80,
      align: 'center',
    },
    {
      id: 'failureCount',
      label: '실패',
      minWidth: 80,
      align: 'center',
    },
    {
      id: 'sentAt',
      label: '전송일',
      minWidth: 150,
      format: (value: Date) => formatDateTime(value),
      sortable: true,
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        푸시 알림 전송
      </Typography>

      {/* 알림 전송 폼 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          알림 전송
        </Typography>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Stack spacing={2}>
            <Controller
              name="title"
              control={control}
              rules={{ required: '제목은 필수입니다' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="제목"
                  fullWidth
                  error={!!errors.title}
                  helperText={errors.title?.message}
                />
              )}
            />
            <Controller
              name="body"
              control={control}
              rules={{ required: '내용은 필수입니다' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="내용"
                  fullWidth
                  multiline
                  rows={4}
                  error={!!errors.body}
                  helperText={errors.body?.message}
                />
              )}
            />
            <Controller
              name="targetType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>대상 선택</InputLabel>
                  <Select {...field} label="대상 선택">
                    <MenuItem value="all">전체 사용자</MenuItem>
                    <MenuItem value="enabled">알림 허용 사용자만</MenuItem>
                    <MenuItem value="disabled">알림 비허용 사용자만</MenuItem>
                    <MenuItem value="specific">특정 사용자</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
            <Autocomplete
              multiple
              options={usersData?.items || []}
              getOptionLabel={option => `${option.email} (${option.username})`}
              value={selectedUsers}
              onChange={(_, newValue) => {
                setSelectedUsers(newValue);
                setValue(
                  'targetUserIds',
                  newValue.map(user => user.id)
                );
              }}
              onInputChange={(_, newInputValue) => {
                setUserSearch(newInputValue);
              }}
              disabled={targetType !== 'specific'}
              renderInput={params => (
                <TextField
                  {...params}
                  label="대상 사용자 선택"
                  placeholder="이메일 또는 이름으로 검색"
                  helperText="특정 사용자를 선택하려면 이메일 또는 이름으로 검색하세요"
                />
              )}
              noOptionsText="사용자를 찾을 수 없습니다"
              loadingText="검색 중..."
            />
            <Button
              type="submit"
              variant="contained"
              startIcon={<SendIcon />}
              disabled={sendMutation.isPending}
              sx={{ alignSelf: 'flex-start' }}
            >
              {sendMutation.isPending ? '전송 중...' : '알림 전송'}
            </Button>
          </Stack>
        </form>
      </Paper>

      {/* 전송 결과 */}
      {sendResult && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              전송 결과
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  총 대상
                </Typography>
                <Typography variant="h5">{sendResult.totalCount}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  성공
                </Typography>
                <Typography variant="h5" color="success.main">
                  {sendResult.successCount}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  실패
                </Typography>
                <Typography variant="h5" color="error.main">
                  {sendResult.failureCount}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* 알림 전송 이력 */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        알림 전송 이력
      </Typography>
      <DataTable
        columns={columns}
        data={(historyData?.items || []).map(item => ({ ...item, id: item.id }))}
        total={historyData?.total || 0}
        page={filters.page}
        rowsPerPage={filters.limit}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={isLoading}
      />

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PushNotificationPage;
